// src/hooks/layout/useElectricalLayout.js
//
// Elektrik paftasi layout hook'u — dokuman yerlesimi ve sahne yonetimi.
// Agir mantik src/utils/layout/ altina cikarildi.

import { useEffect, useRef, useState, useMemo } from 'react';
import * as THREE from 'three';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { getFloorSortOrder, updateFloorPlan, selectProjectMeshSize } from '../../redux/floorPlanSlice';
import { selectGroundingById } from '../../redux/groundingSlice';
import { selectActiveTemplateId, selectActiveNotlarTemplateId } from '../../redux/baslikTemplateSlice';
import { getActiveBaslikDefaults } from '../../redux/baslikSlice';
import { store as appStore } from '../../redux/store';
import { flog } from '../../utils/forensics';
import { setGlobalDocumentBounds, getActiveFloorPlanId, getGlobalDocumentBounds } from '../../utils/getActiveFloorPlanId';
import { selectProjectType } from '../../redux/projectTypeSlice';
import { LAYOUT_GROUP_ORDER, DOC_NAME_TO_GROUP, DEFAULT_PINNED_DOCS, FALLBACK_LAYOUT_GROUP, DEFAULT_LAYOUT_SETTINGS, PAPER_HEIGHT_MULTIPLIER } from '../../constants/projectTypes';
import { selectPanoDetayConfig, selectEnergyRoomConfirmed, selectClippedSegments, selectPanels as selectEnergyRoomPanels, selectDrawingScale, selectDimSettings, selectCustomDimSettings, selectCustomDimensions, selectFrameMargin } from '../../redux/energyRoomSlice';
import { selectPanelTypes } from '../../redux/panelConfigSlice';
import { sweepTesisatToFloorPlanMeshes } from '../../modules/fpSync';
import {
  INITIAL_DEFS, STANDARD_HEIGHTS, MULTIPLIER, OLE_ACTUAL_H_CACHE,
  hideOutlierMeshes,
  rescueTesisatFromMainFrame, disposeMainFrame,
  tryPlaceInCol, positionColumns, createMainFrameGroup, compactColumn,
  repositionLayout,
  buildDocumentDefs,
  buildSingleDocument,
  applyManualGroupOrder,
} from '../../modules/documents';
import { selectManualGroupOrder } from '../../redux/documentLayoutSlice';
// [SAVE-LOAD-REWRITE] shouldSkipForSnapshot kaldırıldı — snapshot eksik
// içerikle kaydedildiğinden artık Redux'tan tam rebuild yapıyoruz, scene'deki
// snapshot mainFrame'i staleMainFrames temizliği ile dispose ederek tek
// mainFrame garanti ediliyor.

let MAIN_FRAME_HEIGHT = STANDARD_HEIGHTS[0] * MULTIPLIER;

// Debug log'ları (tip dağılımı hesabı + [LOAD:...] console.log) her layout
// tetiklemesinde (updateCounter → çizim sırasında sık) tüm scene.children'ı gezip
// JSON.stringify yapıyordu — saf overhead. Sadece teşhis gerektiğinde true yap.
const LAYOUT_DEBUG = false;

export const useElectricalLayout = (scenesRef, sceneTabId) => {
  const TARGET_TAB = sceneTabId || 'elektrik';
  const FLOOR_PLAN_TAB = 'elektrik';
  const [documentDefs, setDocumentDefs] = useState(INITIAL_DEFS);
  const allGroupsRef = useRef([]);
  const documentBoundsRef = useRef([]);
  const prevContentDepsRef = useRef('');
  const layoutColsRef = useRef(null);
  const skipCleanupRef = useRef(false);
  const mainFrameRef = useRef(null);
  const boundsRef = useRef({ frameWidth: 0, frameHeight: 0 });

  // Selectors
  const simpleSupply = useSelector(s => s.simpleSupply);
  const updateCounter = useSelector(s => s.utils.updateCounter);
  const activeBaslikTemplateId = useSelector(selectActiveTemplateId);
  const activeNotlarTemplateId = useSelector(selectActiveNotlarTemplateId);
  // Başlık verisi (aktif pafta) değişince başlık sahnede yenilensin. Her tuş
  // vuruşunda tam rebuild (tesisat rescue dahil) yapmamak için DEBOUNCE'la:
  // yalnız yazma durunca (~350ms) sinyali güncelle → contentDeps değişir → rebuild.
  const baslikData = useSelector(getActiveBaslikDefaults);
  const [baslikSignal, setBaslikSignal] = useState('');
  useEffect(() => {
    const id = setTimeout(() => setBaslikSignal(JSON.stringify(baslikData || {})), 350);
    return () => clearTimeout(id);
  }, [baslikData]);
  const pinnedDocsList = useSelector(s => s.project?.projectMetadata?.pinnedDocs || null, shallowEqual);
  const layoutSettings = useSelector(s => ({ ...DEFAULT_LAYOUT_SETTINGS, ...(s.project?.projectMetadata?.layoutSettings || {}) }), shallowEqual);
  const elektrikAllIds = useSelector(s => s.floorPlans.tabs?.[FLOOR_PLAN_TAB]?.allIds || [], shallowEqual);
  const elektrikById = useSelector(s => s.floorPlans.tabs?.[FLOOR_PLAN_TAB]?.byId);
  const mimariById = useSelector(s => s.floorPlans.tabs?.mimari?.byId);
  const sitePlan = useSelector(s => s.utils.sitePlan);
  const projectMeshSize = useSelector(selectProjectMeshSize, shallowEqual);
  const projectType = useSelector(selectProjectType);
  const mainGrounding = useSelector(selectGroundingById('main_grounding'));
  const panoDetayConfig = useSelector(selectPanoDetayConfig);
  const energyRoomConfirmed = useSelector(selectEnergyRoomConfirmed);
  const erClippedSegments = useSelector(selectClippedSegments);
  const erPanels = useSelector(selectEnergyRoomPanels);
  const erDrawingScale = useSelector(selectDrawingScale);
  const erDimSettings = useSelector(selectDimSettings);
  const erCustomDimSettings = useSelector(selectCustomDimSettings);
  const erCustomDimensions = useSelector(selectCustomDimensions);
  const erFrameMargin = useSelector(selectFrameMargin);
  const panelTypes = useSelector(selectPanelTypes);
  const manualGroupOrder = useSelector(selectManualGroupOrder);
  const dispatch = useDispatch();
  // Drag hook'unun (useDocumentMove) live reposition için ihtiyaç duyduğu
  // layout state'i — groupCols, allCols, mainFrameGroup, layout config.
  const layoutStateRef = useRef(null);

  const floorPlans = useMemo(() => {
    if (!elektrikAllIds.length || !elektrikById) return [];
    return elektrikAllIds
      .map(id => { const p = elektrikById[id]; if (!p) return null; const mp = mimariById?.[p.sourceFloorPlanId || p.id]; return { ...p, group: mp?.group || p.group || null }; })
      .filter(Boolean)
      .sort((a, b) => getFloorSortOrder(a.type, a.number) - getFloorSortOrder(b.type, b.number));
  }, [elektrikAllIds, elektrikById, mimariById]);

  // Tek Hat Şeması gruplama için panel sayısı takibi (MULTI_METER)
  const panelCountKey = simpleSupply?.components?.filter(
    c => c.kind === 'dist_panel' || c.kind === 'panel'
  ).length ?? 0;

  useEffect(() => {
    if (!floorPlans || floorPlans.length === 0) return;
    const updatedDefs = buildDocumentDefs(floorPlans, sitePlan, MAIN_FRAME_HEIGHT, projectType, simpleSupply);
    setDocumentDefs(prev => {
      if (prev.length !== updatedDefs.length) return updatedDefs;
      if (prev.filter(d => d.name === 'Kat Plan\u0131').length !== updatedDefs.filter(d => d.name === 'Kat Plan\u0131').length) return updatedDefs;
      const pSP = prev.find(d => d.name === 'Vaziyet Plan\u0131'), nSP = updatedDefs.find(d => d.name === 'Vaziyet Plan\u0131');
      if (pSP !== nSP && (!pSP || !nSP || pSP.w !== nSP.w || pSP.h !== nSP.h)) return updatedDefs;
      // Tek Hat Şeması grup sayısı değişmişse yenile
      if (prev.filter(d => d.name === 'TekHat  \u015eemas\u0131').length !== updatedDefs.filter(d => d.name === 'TekHat  \u015eemas\u0131').length) return updatedDefs;
      return prev;
    });
  }, [floorPlans?.length, sitePlan?.width, sitePlan?.height, sitePlan?.id, projectType, panelCountKey]);

  useEffect(() => {
    if (!scenesRef?.current || !Array.isArray(documentDefs)) return;
    const FLOOR_DOC_NAMES = ['Kat Plan\u0131', 'Vaziyet Plan\u0131', 'Ground Floor'];
    const defsToRender = updateCounter === 0 ? documentDefs.filter(d => FLOOR_DOC_NAMES.includes(d.name)) : documentDefs;
    if (defsToRender.length === 0) return;
    if (!scenesRef.current[TARGET_TAB]) scenesRef.current[TARGET_TAB] = new THREE.Scene();
    const scene = scenesRef.current[TARGET_TAB];

    // [SAVE-LOAD-REWRITE] Snapshot/eski mainFrame dispose — AMA önce tesisat
    // rescue edilir. ÖNCEKİ "rescue YOK" kararının gerçek sebebi 489 DXF-çizgisinin
    // wire (type='wire') sanılıp rescue tarafından scene root'a çöp olarak
    // taşınmasıydı. O çöpün KAYNAĞI (isWireLike DXF polyline'ları wire sayıyordu)
    // artık düzeltildi → rescue yalnızca GERÇEK tesisat/wire mesh'lerini kurtarır.
    // Rescue olmadan dispose, snapshot mainFrame içindeki tesisatı (box/panel/
    // socket) da yok ediyordu (kullanıcı: "box/mainPanel/panel hiç oluşmadı").
    const staleMainFrames = scene.children.filter(c =>
      c?.userData?.isMainFrame === true && c !== mainFrameRef.current
    );
    if (LAYOUT_DEBUG) {
      // Tip dağılımı — scene.children'da hangi tip ve userData.type'tan kaç tane
      const typeBreakdown = {};
      const udTypeBreakdown = {};
      const sourceBreakdown = {};
      scene.children.forEach(c => {
        const t = c?.type || 'unknown';
        typeBreakdown[t] = (typeBreakdown[t] || 0) + 1;
        const ud = c?.userData?.type || '(none)';
        udTypeBreakdown[ud] = (udTypeBreakdown[ud] || 0) + 1;
        const src = c?.userData?.source || c?.userData?.parent || '(none)';
        sourceBreakdown[src] = (sourceBreakdown[src] || 0) + 1;
      });
      // eslint-disable-next-line no-console
      console.log('[LOAD:useElectricalLayout-trigger]', JSON.stringify({
        tab: TARGET_TAB,
        sceneChildrenBefore: scene.children.length,
        staleMainFrameCount: staleMainFrames.length,
        mainFrameRefIsNull: !mainFrameRef.current,
        typeBreakdown,
        udTypeBreakdown,
        sourceBreakdown,
      }));
    }
    if (staleMainFrames.length > 0) {
      for (const sm of staleMainFrames) {
        try {
          // Gerçek tesisat/wire'ları scene root'a kurtar (sweep aşağıda yeni
          // fpMesh'e bağlar). Sadece bunu yapan dispose tesisatı yok ediyordu.
          rescueTesisatFromMainFrame(scene, sm);
          sm.traverse?.(c => {
            if (c.geometry) c.geometry.dispose?.();
            if (c.material) {
              if (Array.isArray(c.material)) c.material.forEach(m => m.dispose?.());
              else c.material.dispose?.();
            }
          });
          sm.parent?.remove(sm);
        } catch (_) {}
      }
      if (LAYOUT_DEBUG) {
        // eslint-disable-next-line no-console
        console.log('[LOAD:useElectricalLayout-stale-removed]', JSON.stringify({
          sceneChildrenAfter: scene.children.length,
        }));
      }
    }

    const DOC_OFFSET = Math.max(0, Number(layoutSettings.docSpacing) || 50);
    const PAPER_SIZE = layoutSettings.paperSize || 'auto';
    if (PAPER_SIZE !== 'auto') { const f = parseInt(PAPER_SIZE, 10) * PAPER_HEIGHT_MULTIPLIER; if (Number.isFinite(f) && f > 0) MAIN_FRAME_HEIGHT = f; }
    else { const maxH = Math.max(...defsToRender.map(d => d.h)); for (const w of STANDARD_HEIGHTS) { if (maxH <= w * MULTIPLIER) { MAIN_FRAME_HEIGHT = w * MULTIPLIER; break; } } }
    const maxColH = MAIN_FRAME_HEIGHT - 2 * DOC_OFFSET;

    // manualGroupOrder contentDeps'e dahil — değiştiğinde isRepos=false olur,
    // tam rebuild tetiklenir ve applyManualGroupOrder uygulanır. Aksi halde
    // repositionLayout eski sırada doc'ları yerleştirir, drag'in etkisi
    // kaybolur (kullanıcı raporu: "bırakınca eski sıraya dönüyor").
    const contentDeps = `${updateCounter}|${documentDefs.length}|${projectType}|${JSON.stringify(panoDetayConfig)}|${energyRoomConfirmed}|${activeBaslikTemplateId}|${activeNotlarTemplateId}|${pinnedDocsList}|${JSON.stringify(manualGroupOrder)}|${baslikSignal}`;
    const isRepos = prevContentDepsRef.current === contentDeps && layoutColsRef.current && mainFrameRef.current && allGroupsRef.current.length > 0;
    prevContentDepsRef.current = contentDeps;

    if (isRepos) {
      const r = repositionLayout(mainFrameRef.current, layoutColsRef.current, layoutSettings, maxColH, DOC_OFFSET, MAIN_FRAME_HEIGHT);
      documentBoundsRef.current = r.documentBounds; boundsRef.current = r.bounds; skipCleanupRef.current = true;
      const old = mainFrameRef.current;
      return () => { if (skipCleanupRef.current) { skipCleanupRef.current = false; return; } rescueTesisatFromMainFrame(scene, old); disposeMainFrame(scene, old); mainFrameRef.current = null; allGroupsRef.current = []; };
    }

    if (mainFrameRef.current) { rescueTesisatFromMainFrame(scene, mainFrameRef.current); disposeMainFrame(scene, mainFrameRef.current); mainFrameRef.current = null; }
    const mainFrameGroup = new THREE.Group();
    // [FAZ 2] Snapshot guard'ın tanıyabilmesi için işaretle
    mainFrameGroup.userData.isMainFrame = true;
    const pm = appStore.getState().project?.projectMetadata || {};
    const us = appStore.getState().settings || {};
    const pinnedDocs = new Set(Array.isArray(pm.pinnedDocs) ? pm.pinnedDocs : Array.isArray(us.pinnedDocs) ? us.pinnedDocs : DEFAULT_PINNED_DOCS);
    const groupCols = {}; LAYOUT_GROUP_ORDER.forEach(g => { groupCols[g] = []; });
    const pinnedCols = [], pendingOle = [], newDocBounds = [];
    defsToRender.forEach(d => { if (d.name === 'Kat Plan\u0131' || d.name === 'Ground Floor') d.h = maxColH; });
    const erData = { erClippedSegments, erPanels, erDrawingScale, erDimSettings, erCustomDimSettings, erCustomDimensions, erFrameMargin };
    const buildParams = { simpleSupply, floorPlans, projectType, mainGrounding, projectMeshSize, dispatch, updateFloorPlan, FLOOR_PLAN_TAB, energyRoomConfirmed, erData, panoDetayConfig, panelTypes, appStore };

    // Manuel grup-i\u00e7i s\u0131ralamay\u0131 uygula (kullan\u0131c\u0131 M ile s\u00fcr\u00fckleyip b\u0131rakt\u0131\u011f\u0131
    // doc'lar i\u00e7in). Pin'li ve dinamik doc'lar etkilenmez. S\u0131ra de\u011fi\u015fimi
    // tryPlaceInCol greedy packing \u00fczerinden farkl\u0131 s\u00fctun yerle\u015fimine yol a\u00e7ar.
    const orderedDefs = applyManualGroupOrder(defsToRender, manualGroupOrder, pinnedDocs);

    orderedDefs.forEach((docDef) => {
      if (docDef.name === "Enerji Odasi" && !energyRoomConfirmed) return;
      // Tek bir doküman build'i patlarsa TÜM mainFrame'i düşürmesin. try-catch
      // yokken forEach exception'da kırılıyor, aşağıdaki scene.add(mainFrameGroup)
      // hiç çağrılmıyor → eski mainFrame dispose edildiği için TÜM dokümanlar +
      // mainFrame kayboluyordu (kullanıcı: priz hattı + Enter → her şey uçtu).
      // Hata loglanır ki kök neden (hangi doc, hangi exception) görünür kalsın.
      let _built;
      try {
        _built = buildSingleDocument(docDef, buildParams);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(`[useElectricalLayout] doküman build hatası, atlandı: ${docDef?.name}`, err);
        return;
      }
      const { documentGroup, olePromise, isEmpty } = _built;
      if (olePromise) pendingOle.push(olePromise);
      if (isEmpty) return;
      const docW = docDef.w, docH = docDef.h, isPinned = pinnedDocs.has(docDef.name);
      const groupKey = isPinned ? null : (DOC_NAME_TO_GROUP[docDef.name] || FALLBACK_LAYOUT_GROUP);
      let chosenCol, chosenPos;
      if (isPinned) { chosenCol = { x: 0, height: 0, maxWidth: 0, docs: [] }; pinnedCols.push(chosenCol); chosenPos = { subX: 0, subY: 0 }; }
      else { const tc = groupCols[groupKey]; chosenCol = tc[tc.length - 1]; chosenPos = chosenCol ? tryPlaceInCol(chosenCol, docW, docH, maxColH, DOC_OFFSET) : null; if (!chosenPos) { chosenCol = { x: 0, height: 0, maxWidth: 0, docs: [] }; tc.push(chosenCol); chosenPos = tryPlaceInCol(chosenCol, docW, docH, maxColH, DOC_OFFSET) || { subX: 0, subY: 0 }; } }
      chosenCol.docs.push({ group: documentGroup, docDef, w: docW, h: docH, subX: chosenPos.subX, subY: chosenPos.subY });
      chosenCol.height = Math.max(chosenCol.height, chosenPos.subY + docH);
      chosenCol.maxWidth = Math.max(chosenCol.maxWidth, chosenPos.subX + docW);
      mainFrameGroup.add(documentGroup); allGroupsRef.current.push(documentGroup);
    });

    const { allCols, totalWidth } = positionColumns(groupCols, pinnedCols, layoutSettings, maxColH, DOC_OFFSET);
    const DIR = layoutSettings.direction || 'ltr', VA = layoutSettings.verticalAlign || 'bottom';
    if (pendingOle.length > 0) {
      Promise.all(pendingOle).then(results => {
        results.forEach(r => { if (!r) return; if (r.actualH > 50) OLE_ACTUAL_H_CACHE.set(r.docDef.name, r.actualH); if (r.actualH > 50 && Math.abs(r.actualH - r.docDef.h) > 5) r.docDef.h = Math.ceil(r.actualH); });
        allCols.forEach(c => compactColumn(c, DOC_OFFSET, maxColH, VA, DIR, totalWidth));
      });
    } else { requestAnimationFrame(() => allCols.forEach(c => compactColumn(c, DOC_OFFSET, maxColH, VA, DIR, totalWidth))); }

    allCols.forEach(col => col.docs.forEach(item => {
      if (item.docDef.name === 'Kat Plan\u0131' && item.docDef.floorPlanId) {
        const p = item.group.position;
        newDocBounds.push({ floorPlanId: item.docDef.floorPlanId, name: item.docDef.displayName || item.docDef.name, minX: p.x, maxX: p.x + item.w, minY: p.y - item.h, maxY: p.y });
      }
    }));
    documentBoundsRef.current = newDocBounds; setGlobalDocumentBounds(newDocBounds);
    const { group: fCG, frameWidth } = createMainFrameGroup(totalWidth, MAIN_FRAME_HEIGHT, DOC_OFFSET);
    if (fCG.children.length > 0) mainFrameGroup.add(fCG);
    hideOutlierMeshes(mainFrameGroup, 0, 'global mainFrameGroup');
    scene.add(mainFrameGroup);
    if (LAYOUT_DEBUG) {
      // eslint-disable-next-line no-console
      console.log('[LOAD:useElectricalLayout-mainFrame-added]', JSON.stringify({
        tab: TARGET_TAB,
        sceneChildren: scene.children.length,
        mainFrameChildren: mainFrameGroup.children.length,
        isMainFrameObjsInScene: scene.children.filter(c => c?.userData?.isMainFrame === true).length,
      }));
    }
    try { const b = getGlobalDocumentBounds(); const r = sweepTesisatToFloorPlanMeshes(scene, wp => getActiveFloorPlanId(wp, b)); flog('FAZ14_SWEEP_SONUCU', { attached: r.attached, skipped: r.skipped, missing: r.missing }); } catch (err) { console.warn('[tesisat-sweep]', err); }
    boundsRef.current = { frameWidth, frameHeight: MAIN_FRAME_HEIGHT, minX: 0, maxX: frameWidth, minY: -MAIN_FRAME_HEIGHT, maxY: 0 };
    mainFrameGroup.userData.frameBounds = { frameWidth, frameHeight: MAIN_FRAME_HEIGHT, docOffset: DOC_OFFSET };
    mainFrameRef.current = mainFrameGroup;
    layoutColsRef.current = allCols.flatMap(col => col.docs.map(d => ({ group: d.group, docDef: d.docDef, w: d.w, h: d.h, isPinned: pinnedDocs.has(d.docDef.name), groupKey: DOC_NAME_TO_GROUP[d.docDef.name] || FALLBACK_LAYOUT_GROUP })));
    // Drag hook için layout state snapshot — sürükleme sırasında live reposition
    // gerekirse buradan groupCols / allCols / mainFrameGroup okunur.
    layoutStateRef.current = {
      mainFrameGroup,
      groupCols,
      pinnedCols,
      allCols,
      maxColH,
      DOC_OFFSET,
      layoutSettings,
      totalWidth,
      pinnedDocs,
    };
    skipCleanupRef.current = true;
    return () => { if (skipCleanupRef.current) { skipCleanupRef.current = false; return; } rescueTesisatFromMainFrame(scene, mainFrameGroup); disposeMainFrame(scene, mainFrameGroup); mainFrameRef.current = null; allGroupsRef.current = []; layoutStateRef.current = null; };
  }, [updateCounter, documentDefs, projectType, panoDetayConfig, energyRoomConfirmed, activeBaslikTemplateId, activeNotlarTemplateId, pinnedDocsList, layoutSettings, manualGroupOrder, baslikSignal]);

  return { allGroupsRef, boundsRef, documentBoundsRef, layoutStateRef };
};
